import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

// gloss
const stackVariants = cva('flex flex-col', {
  variants: {
    gap: {
      0: 'gap-0',
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      6: 'gap-6',
      8: 'gap-8',
      12: 'gap-12',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
    mdDirection: {
      row: 'md:flex-row',
      col: 'md:flex-col',
    },
    mdAlign: {
      start: 'md:items-start',
      center: 'md:items-center',
      end: 'md:items-end',
      stretch: 'md:items-stretch',
    },
    mdJustify: {
      start: 'md:justify-start',
      center: 'md:justify-center',
      end: 'md:justify-end',
      between: 'md:justify-between',
    },
    mdGap: {
      0: 'md:gap-0',
      1: 'md:gap-1',
      2: 'md:gap-2',
      3: 'md:gap-3',
      4: 'md:gap-4',
      6: 'md:gap-6',
      8: 'md:gap-8',
      12: 'md:gap-12',
    },
  },
  defaultVariants: {
    gap: 4,
    align: 'stretch',
  },
});

// gloss
type StackProps = useRender.ComponentProps<'div'> & VariantProps<typeof stackVariants>;

const Stack = ({
  className,
  gap = 4,
  align = 'stretch',
  justify,
  mdDirection,
  mdAlign,
  mdJustify,
  mdGap,
  render,
  ...props
}: StackProps) => {
  return useRender({
    defaultTagName: 'div',
    props: mergeProps<'div'>(
      {
        className: cn(stackVariants({ gap, align, justify, mdDirection, mdAlign, mdJustify, mdGap, className })),
      },
      props,
    ),
    render,
    state: {
      slot: 'stack',
    },
  });
};

export { Stack, stackVariants };
